import React, {
    useCallback, useState, useContext, useMemo, useEffect,
} from 'react';
import { useFragment, useQuery } from '@apollo/client';
import { Portal } from 'react-portal';
import { Transition } from 'react-transition-group';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { useRouterState } from 'app/hooks/useRouterState';
import { analyticService } from 'global/services';
import { LocaleContext } from 'app/containers/LocaleProvider';
import { getAgeGateLocales } from 'app/views/const/getAgeGateLocales';
import { selectedPeriodVar } from 'app/apollo/reaction';
import { dishes as kozelDishes } from 'global/const/kozel';

import USER_QUERY from 'app/graphql/network/auth/userQuery.graphql';
import { getConfirmCustomizationLocales }
    from 'app/views/Basket/components/BasketCustomizationTags.DEPRECATED/basket-customization-locales';
import { DialogAnimated } from 'app/components/Dialog';
import { useCustomContradictionDefine } from 'app/views/Basket/hooks/useCustomContradictionDefine';
import { hasDishesInCart } from 'app/views/Checkout/hooks/useReadyToEatContradictionCheck';
import { useReadyToEatContradictionDefine } from 'app/views/Checkout/hooks/useReadyToEatContradictionDefine';
import { BASKET_ALL_DISHES_FRAGMENT } from './graphql/basketDishesFragment.graphql';
import { StepLayout } from '../StepLayout/StepLayout';
import { SingleStepDeliveryInfoArea } from './components/SingleStepDeliveryInfoArea';
import { SingleStepCheckArea } from './components/SingleStepCheckArea';
import { StepPayButtonContent } from '../StepPayment/components/StepPayButtonContent';
import { PaperRecipeTogleRow } from './components/PaperRecipeRow';
import { CheckoutStoreContext } from '../../store/checkoutStore.context';

import { StepAuth } from '../StepAuth/StepAuth';
import { StepAddress } from '../StepAddress/StepAddress';
import { StepDatetime } from '../StepDatetime/StepDatetime';
import { StepPayment } from '../StepPayment/StepPayment';

import {
    STEP_PHONE,
    STEP_ADDRESS,
    STEP_DATETIME,
    STEP_PAYMENT,
    RENDER_CONTEXT_SINGLE_STEP,
    EVENTS,
} from '../stepByStepCheckoutConst';
import { useAuthState } from '../stepByStepCheckoutHooks';
import { usePaperRecipe } from '../../../Hooks/usePaperRecipe/usePaperRecipe';
import { locales, readyToEatDialogLocales } from './single-step-checkout-locales';

import '../StepByStepCheckout/step-by-step-checkout.scss';
import { OrderSupplementsToggles, PaperRecipeRowV2, TableSetting } from '../../additionalOrderOptions';
import { URLUpdater } from 'global/utils';

const STEPS_COMPONENTS = {
    [STEP_PHONE]: StepAuth,
    [STEP_ADDRESS]: StepAddress,
    [STEP_DATETIME]: StepDatetime,
    [STEP_PAYMENT]: StepPayment,
};


const EDIT_OPEN_TIMEOUT = 600;


/**
 * @description Шаг в одностраничном checkout открывается и закрывается поверх
 * основного экрана. `editingStep` обнуляется отдельным таймером, поэтому он может
 * обновиться раньше, чем завершится анимация закрытия: по нему нельзя судить, что
 * шаг действительно исчез с экрана. Поэтому отдельно отслеживаем завершение
 * перехода через onExited, а не по флагу шага.
 */
const useEditingStepState = () => {
    const [editingStep, setEditingStep] = useState(null);
    const [isStepEditing, setIsStepEditing] = useState(false);
    const [isStepTransitionEnded, setIsStepTransitionEnded] = useState(true);

    const handleOpenStep = useCallback((step) => {
        setIsStepTransitionEnded(false);
        setEditingStep(step);
        setIsStepEditing(true);
    }, []);

    const handleCloseStep = useCallback(() => {
        setIsStepEditing(false);
        setTimeout(() => {
            setEditingStep(null);
        }, EDIT_OPEN_TIMEOUT);
    }, []);

    const handleStepTransitionEnded = useCallback(() => {
        setIsStepTransitionEnded(true);
    }, []);

    return {
        editingStep,
        isStepEditing,
        isStepTransitionEnded,
        handleOpenStep,
        handleCloseStep,
        handleStepTransitionEnded,
    };
};

export function SingleStepCheckout(props) {
    const {
        closeCheckout,
        containerTransitionState,
        [STEP_PAYMENT]: paymentProps,
        togglePaperRecipeSwitch,
        setIsAddressChanged,
        isAddressPopupNeeded,
        handleOpenAddressInfoPopup,
        setIsCustomizationIgnored,
    } = props;

    const { paperRecipeDisabled: isPaperRecipeDisabled } = usePaperRecipe();
    const { dispatch } = useContext(CheckoutStoreContext);

    const userQuery = useQuery(USER_QUERY, {
        onCompleted: () => {
            setIsAddressChanged(true);
        },
        fetchPolicy: 'cache-first',
        context: {
            message: 'checkout:refetch:SingleStepCheckout',
        },
    });

    // const { data: basketData } = useFragment({
    //     from: {
    //         __ref: `cart:new:${selectedPeriodVar()}`,
    //     },
    //     fragment: BASKET_ALL_DISHES_FRAGMENT,
    // });


    // const dishIds = useMemo(
    //     () => basketData?.sections
    //         ?.map((section) => [...section.items])
    //         ?.flat()
    //         ?.map((item) => item.dish_id),
    //     [basketData],
    // );

    const [routerState] = useRouterState();
    const { history, location } = routerState;
    const [isCustomizationDialogClosed, setIsCustomizationDialogClosed] = useState(false);

    const {
        isCustomizationDialogShown,
        setIsCustomizationDialogShown,
        dialogIngridient,
    } = useCustomContradictionDefine({ userQuery, cart: paymentProps.cart });

    const [isInitialAddressStepHandled, setIsInitialAddressStepHandled] = useState(false);
    const [isAddressStepAutoOpened, setIsAddressStepAutoOpened] = useState(false);
    const [isAgeGatePopupShown, setIsAgeGatePopupShown] = useState(false);

    const {
        cart,
        paymentMethod,
        handlePay,
    } = paymentProps;

    const isTimeIntervalsDataFetched = useMemo(() => {
        const {
            [STEP_DATETIME]: {
                timeIntervals,
                selectDay,
            },
        } = props;

        const hasData = timeIntervals?.length > 0 && Boolean(selectDay);

        return hasData;
    }, [props]);

    const { locale } = useContext(LocaleContext);
    const l = locales[locale];

    const [isNextStepLoading, setIsNextStepLoading] = useState(false);

    const {
        editingStep,
        isStepEditing,
        isStepTransitionEnded,
        handleOpenStep,
        handleCloseStep,
        handleStepTransitionEnded,
    } = useEditingStepState();

    const isAddressStepOpened = editingStep === STEP_ADDRESS;
    /**
     * Финальный экран одностраничного checkout: экран доехал на место, адрес уже
     * пройден, ни один шаг не открыт и анимация его закрытия завершилась. Только
     * тогда пользователю показывают напоминание о готовых блюдах — один раз, а не
     * поверх уезжающего шага, проезжающего на экране одностраничного checkout или
     * любого другого открытого шага.
     */
    const isFinalScreenShown = isInitialAddressStepHandled
        && containerTransitionState === 'entered'
        && editingStep === null
        && isStepTransitionEnded;
    const {
        isReadyToEatDialogShown,
        handleRemoveReadyToEatDishes,
        handleRejectReadyToEatDialog,
    } = useReadyToEatContradictionDefine({
        cart: paymentProps.cart,
        dispatch,
        pathname: location.pathname,
        isAddressStepOpened,
        isFinalScreenShown,
        isSingleStepCheckout: true,
    });

    const [authState, setAuthState] = useAuthState(userQuery);

    /* HANDLERS  */

    const handleCloseAllSteps = useCallback(() => {
        analyticService.push({ eventName: 'Checkout_Close_Onepager' });
        closeCheckout();
    }, [closeCheckout]);

    const handleSubmit = useCallback(() => {
        handlePay();
        setIsNextStepLoading(true);
    }, [handlePay]);

    const handleCloseStepWithPushEvent = useCallback(() => {
        analyticService.push({ eventName: EVENTS[editingStep].backFromEditing });
        handleCloseStep();
    }, [editingStep, handleCloseStep]);

    /**
     * @description Пользователь согласился удалить готовые блюда. Все шаги уже
     * свёрнуты на финальный экран, поэтому при успешном удалении пользователь
     * остаётся на нём: противоречие снято и кнопка оплаты разблокирована. В
     * корзину отправляем только если блюд не осталось вовсе. Если запрос не
     * прошёл, остаёмся на месте, не решая за пользователя.
     */
    const handleConfirmReadyToEatDialog = useCallback(async () => {
        const nextCart = await handleRemoveReadyToEatDishes();

        if (!nextCart) return;
        if (hasDishesInCart(nextCart)) return;

        history.push('/basket');
    }, [handleRemoveReadyToEatDishes, history]);

    /* */

    const Component = STEPS_COMPONENTS[editingStep];
    const stepProps = props[editingStep]; // eslint-disable-line

    const childProps = {
        // common props
        renderContext: RENDER_CONTEXT_SINGLE_STEP,
        handleOpenNextStep: handleCloseStep,
        handleCloseCurrentStep: handleCloseStepWithPushEvent,
        isNextStepButtonVisible: true,
        headerTitle: l[editingStep],

        ...stepProps,

        // authStepProps
        authState,
        setAuthState,
        handleUserEnteredCode: () => { },
        switchToSingleStepCheckout: handleCloseStep,

        // PaymentStepProps
        nextStepButtonType: 'regular',
    };

    const layoutContainerClasses = cn({
        'single-step__container': true,
        'is-step-editing': isStepEditing,
    });

    useEffect(() => {
        setIsInitialAddressStepHandled(true);
        // DID MOUNT
    }, []);

    useEffect(() => {
        if (isAddressStepAutoOpened) return;
        if (isAddressPopupNeeded) {
            handleOpenAddressInfoPopup();
            handleOpenStep(STEP_ADDRESS);
            setIsAddressStepAutoOpened(true);
        }
    }, [isAddressPopupNeeded, isAddressStepAutoOpened, handleOpenAddressInfoPopup, handleOpenStep]);


    useEffect(() => {
        URLUpdater({ checkout_type: '' });
    }, []);

    if (userQuery.loading) {
        return null;
    }

    const dialogTexts = dialogIngridient
        ? getConfirmCustomizationLocales(dialogIngridient[locale])[locale].dialog.success
        : null;

    const readyToEatDialogTexts = readyToEatDialogLocales[locale];

    const ageGateDialogTexts = getAgeGateLocales()[locale].dialog.success;

    return (
        <>
            <div styleName={layoutContainerClasses}>
                <StepLayout
                    fillContent={false}
                    isNextStepButtonDisabled={!isTimeIntervalsDataFetched || isNextStepLoading}
                    isNextStepButtonVisible
                    isNextStepLoading={isNextStepLoading}
                    title={l.headerTitle}
                    currentStepNumber={2}
                    handleCloseCurrentStep={handleCloseAllSteps}
                    handleOpenNextStep={handleSubmit}
                    nextStepButtonContent={(
                        <StepPayButtonContent
                            cart={cart}
                            paymentMethod={paymentMethod}
                        />
                    )}
                >
                    <SingleStepDeliveryInfoArea
                        {...props}
                        handleOpenStep={handleOpenStep}
                    />
                    <div styleName="single-step__recipe-wrapper">
                        <PaperRecipeRowV2
                            realm="checkout"
                            isPaperRecipeDisabled={isPaperRecipeDisabled}
                            togglePaperRecipeSwitch={togglePaperRecipeSwitch}
                        />
                    </div>
                    <SingleStepCheckArea
                        locale={locale}
                        paymentProps={paymentProps}
                    />
                </StepLayout>
            </div>

            <Transition
                in={isStepEditing}
                timeout={EDIT_OPEN_TIMEOUT}
                onExited={handleStepTransitionEnded}
            >
                {(transitionState) => {
                    const containerClasses = cn({
                        'step-by-step__transition': true,
                        [transitionState]: true,
                    });
                    return (
                        <div styleName={containerClasses}>
                            {transitionState !== 'exited' && Component && (
                                <Component
                                    {...childProps}
                                    transitionState={transitionState}
                                />
                            )}
                        </div>
                    );
                }}
            </Transition>

            {dialogTexts && !isCustomizationDialogClosed && (
                <Portal>
                    <DialogAnimated
                        {...dialogTexts}
                        isOpen={Boolean(isCustomizationDialogShown)}
                        onConfirm={() => {
                            setIsCustomizationIgnored(true);
                            setIsCustomizationDialogShown(false);
                            setIsCustomizationDialogClosed(true);
                            dispatch({
                                type: 'SET_IGNORE_DATES_FILTER',
                                payload: { isNeedToIgnoreDatesFilter: true },
                            });
                        }}
                        onReject={() => {
                            setIsCustomizationIgnored(false);
                            setIsCustomizationDialogShown(false);
                            setIsCustomizationDialogClosed(true);
                            history.push('/basket');
                        }}
                    />
                </Portal>
            )}
            <Portal>
                <DialogAnimated
                    {...readyToEatDialogTexts}
                    isOpen={Boolean(isReadyToEatDialogShown)}
                    onConfirm={handleConfirmReadyToEatDialog}
                    onReject={() => {
                        handleRejectReadyToEatDialog();
                    }}
                    rejectSpecificHandler={() => {
                        handleRejectReadyToEatDialog();
                        handleOpenStep(STEP_ADDRESS);
                    }}
                />
            </Portal>
            <Portal>
                <DialogAnimated
                    {...ageGateDialogTexts}
                    isOpen={isAgeGatePopupShown}
                    onConfirm={() => {
                        setIsAgeGatePopupShown(false);
                        handlePay();
                        setIsNextStepLoading(true);
                    }}
                    rejectSpecificHandler={() => {
                        window.location = 'https://elementaree.ru';
                    }}
                    onReject={() => {
                        setIsAgeGatePopupShown(false);
                    }}
                />
            </Portal>
        </>
    );
}

SingleStepCheckout.propTypes = {
    closeCheckout: PropTypes.func.isRequired,
    containerTransitionState: PropTypes.string.isRequired,
    togglePaperRecipeSwitch: PropTypes.func.isRequired,
    toggleTableSettingSwitch: PropTypes.func.isRequired,
};
